// helpers/toActions.js
//
// Action shape:
// {
//   kind:       "pickup" | "deliver"
//   time:       Date
//   info:       { primary: string, secondary: string }
//   status:     string
//   deliveryId: string
//   windowEnd?: Date
//   busInfo?:   object
// }

function toDate(val) {
  if (!val) return null;
  const d = new Date(typeof val === "number" ? val : val);
  return isNaN(d.getTime()) ? null : d;
}

function detailHints(details = {}) {
  const parts = [];
  if (details.floor)        parts.push(`Kerros ${details.floor}`);
  if (details.apartment)    parts.push(`Asunto ${details.apartment}`);
  if (details.building)     parts.push(`Rakennus ${details.building}`);
  if (details.accessCode)   parts.push(`Koodi ${details.accessCode}`);
  if (details.instructions) parts.push(details.instructions);
  return parts.join(" · ");
}

function addressInfo(locationInfo = {}, fallbackAddress = "—") {
  const primary   = locationInfo.address || fallbackAddress;
  const hint      = detailHints(locationInfo.details || {});
  const secondary = [locationInfo.city, hint].filter(Boolean).join(" · ");
  return { primary, secondary };
}

function busInfoToInfo(busInfo) {
  if (!busInfo) return { primary: "—", secondary: "" };
  return {
    primary:   busInfo.busCompany || "Bussi",
    secondary: busInfo.shortName  || "",
  };
}

// Hydrate an action that was stored on a multi-delivery bundle.
// Stored shape (from buildActionsFromStops):
//   { kind, address, city, hint, time, transport, windowEnd, deliveryId?, parentDeliveryId? }
// deliveryId may be missing — caller passes parentId as fallback.
function hydrateStoredAction(a, parentId) {
  const resolvedId = a.deliveryId || a.parentDeliveryId || parentId;

  let info;
  if (a.busInfo) {
    info = busInfoToInfo(a.busInfo);
  } else if (a.info) {
    info = a.info;
  } else {
    // Built from DeliveryStopInput — address + city are flat fields
    const secondary = [a.city, a.hint].filter(Boolean).join(" · ");
    info = { primary: a.address || "—", secondary };
  }

  return {
    kind:       a.kind,
    time:       toDate(a.time),
    info,
    status:     a.status || "active",
    deliveryId: resolvedId,
    ...(a.windowEnd ? { windowEnd: toDate(a.windowEnd) } : {}),
    ...(a.busInfo   ? { busInfo:   a.busInfo }           : {}),
  };
}

export function toActions(deliveries = []) {
  const actions = [];

  for (const d of deliveries) {
    const id     = d.deliveryId || d.id;
    const picked = d.status === "picked";

    // ── Multi-delivery bundle with pre-built actions ──────────────────────────
    // The stored actions array only contains extra stops, so we prepend
    // the origin (pickup) and main destination (deliver) from the parent delivery.
    if (d.deliveryType === "multi-delivery" && Array.isArray(d.actions) && d.actions.length) {

      // Origin pickup
      if (!picked) {
        const pickupTime   = toDate(d.pickupTime ?? d.pickUpTime);
        const latestPickup = toDate(d.latestPickUpTime ?? d.latestPickupTime);
        if (pickupTime) {
          actions.push({
            kind:       "pickup",
            time:       pickupTime,
            info:       addressInfo(d.originInfo || d.fromInfo, d.origin || d.from),
            status:     d.status,
            deliveryId: id,
            ...(d.hasTimeWindow && latestPickup ? { windowEnd: latestPickup } : {}),
          });
        }
      }

      // Main destination deliver
      const targetTime     = toDate(d.targetTime);
      const earliestTarget = toDate(d.earliestTargetTime);
      const deliverTime    = earliestTarget ?? targetTime;
      if (deliverTime) {
        actions.push({
          kind:       "deliver",
          time:       deliverTime,
          info:       addressInfo(d.destinationInfo || d.toInfo, d.destination || d.to),
          status:     d.status,
          deliveryId: id,
          ...(d.hasTimeWindow && earliestTarget && targetTime ? { windowEnd: targetTime } : {}),
        });
      }

      // Extra stops
      for (const a of d.actions) {
        if (a.kind === "pickup" && picked) continue;
        actions.push(hydrateStoredAction(a, id));
      }

      continue;
    }

    // ── Bus wait / swap leg ───────────────────────────────────────────────────
    if (d.deliveryTransportType === "wait") {
      const arrival   = toDate(d.pickupTime);
      const departure = toDate(d.targetTime);

      if (!picked && arrival) {
        actions.push({
          kind:       "pickup",
          time:       arrival,
          info:       busInfoToInfo(d.inboundBusInfo),
          status:     d.status,
          deliveryId: id,
          busInfo:    d.inboundBusInfo || null,
        });
      }
      if (departure) {
        actions.push({
          kind:       "deliver",
          time:       departure,
          info:       busInfoToInfo(d.outboundBusInfo),
          status:     d.status,
          deliveryId: id,
          busInfo:    d.outboundBusInfo || null,
        });
      }
      continue;
    }

    // ── Regular pickup ────────────────────────────────────────────────────────
    if (!picked) {
      const pickupTime   = toDate(d.pickupTime ?? d.pickUpTime);
      const latestPickup = toDate(d.latestPickUpTime ?? d.latestPickupTime);

      if (pickupTime) {
        actions.push({
          kind:       "pickup",
          time:       pickupTime,
          info:       addressInfo(d.originInfo || d.fromInfo, d.origin || d.from),
          status:     d.status,
          deliveryId: id,
          ...(d.hasTimeWindow && latestPickup ? { windowEnd: latestPickup } : {}),
        });
      }
    }

    // ── Regular delivery ──────────────────────────────────────────────────────
    const targetTime     = toDate(d.targetTime);
    const earliestTarget = toDate(d.earliestTargetTime);
    const deliverTime    = earliestTarget ?? targetTime;

    if (deliverTime) {
      actions.push({
        kind:       "deliver",
        time:       deliverTime,
        info:       addressInfo(d.destinationInfo || d.toInfo, d.destination || d.to),
        status:     d.status,
        deliveryId: id,
        ...(d.hasTimeWindow && earliestTarget && targetTime ? { windowEnd: targetTime } : {}),
      });
    }
  }

  actions.sort((a, b) => {
    if (!a.time && !b.time) return 0;
    if (!a.time) return 1;
    if (!b.time) return -1;
    return a.time - b.time;
  });

  return actions;
}